/**
 * Lumia — header group behaviours (vanilla ES2019, no dependencies).
 *
 * - <lh-header-nav>        desktop dropdowns: hover (fine pointers), caret
 *                          button toggles (touch/keyboard), ArrowDown opens,
 *                          Esc closes and returns focus, outside click closes,
 *                          panels flip when they would overflow the viewport.
 * - <lh-drawer-menu>       mobile menu accordion groups (aria-expanded).
 * - <lh-announcement-bar>  optional autoplay rotation (pauses on hover/focus,
 *                          hidden tab, and in the theme editor on block select;
 *                          reduced motion → shows all items statically).
 * - .lh-header             sticky wrapper class + `is-scrolled` shadow.
 * - drawers                aria-expanded on openers, focus the search input
 *                          when #search-drawer opens, close #menu-drawer when
 *                          the viewport grows to desktop.
 * - cart button            falls back to the cart page when no #cart-drawer
 *                          exists or window.Lumia failed to load.
 *
 * The `--lh-header-height` custom property is maintained by global.js (not here).
 * This file is loaded once from layout/theme.liquid <head> (defer, after global.js);
 * the guard below keeps a second execution (theme editor / legacy include) a no-op.
 * predictive-search.js is loaded lazily from here (see loadPredictiveSearch).
 */
(function () {
  'use strict';

  if (window.__lumiaHeaderJs) return;
  window.__lumiaHeaderJs = true;

  var mqDesktop = window.matchMedia('(min-width: 1024px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function onMedia(mq, fn) {
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', fn);
    else if (typeof mq.addListener === 'function') mq.addListener(fn);
  }

  /* ------------------------------------------------------------------------
   * Sticky header: wrapper class + scrolled shadow
   * ---------------------------------------------------------------------- */
  var headers = [];
  var scrollTicking = false;

  function updateScrolled() {
    scrollTicking = false;
    var scrollY = window.scrollY || window.pageYOffset || 0;
    headers = headers.filter(function (h) { return document.body.contains(h); });
    headers.forEach(function (h) {
      // Shadow only once the (sticky) header is actually stuck to the top, i.e.
      // after the announcement bar above it has scrolled away.
      var wrap = h.closest('.shopify-section') || h;
      var stuck = scrollY > 4 && wrap.getBoundingClientRect().top <= 0;
      h.classList.toggle('is-scrolled', stuck);
    });
  }

  function initHeader(header) {
    if (!header || header.hasAttribute('data-lh-header-ready')) return;
    header.setAttribute('data-lh-header-ready', '');
    var wrap = header.closest('.shopify-section');
    if (wrap) {
      wrap.classList.toggle('lh-header-section--sticky', header.classList.contains('lh-header--sticky'));
    }
    headers.push(header);
    updateScrolled();
  }

  window.addEventListener('scroll', function () {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(updateScrolled);
  }, { passive: true });

  /* ------------------------------------------------------------------------
   * Desktop navigation dropdowns
   * ---------------------------------------------------------------------- */
  var HOVER_CLOSE_DELAY = 150;

  var LhHeaderNav = /** @class */ (function () {
    function LhHeaderNav() {
      var self = Reflect.construct(HTMLElement, [], LhHeaderNav);
      self._openItem = null;
      self._closeTimer = null;
      self._onDocumentClick = self._onDocumentClick.bind(self);
      self._onDocumentKeydown = self._onDocumentKeydown.bind(self);
      return self;
    }

    LhHeaderNav.prototype = Object.create(HTMLElement.prototype, { constructor: { value: LhHeaderNav } });

    LhHeaderNav.prototype.connectedCallback = function () {
      var self = this;
      this._items = toArray(this.querySelectorAll('[data-header-item]'));
      this._items.forEach(function (item) { self._bindItem(item); });
      document.addEventListener('click', this._onDocumentClick);
      document.addEventListener('keydown', this._onDocumentKeydown);
    };

    LhHeaderNav.prototype.disconnectedCallback = function () {
      document.removeEventListener('click', this._onDocumentClick);
      document.removeEventListener('keydown', this._onDocumentKeydown);
      window.clearTimeout(this._closeTimer);
    };

    LhHeaderNav.prototype._bindItem = function (item) {
      var self = this;
      var caret = item.querySelector('[data-header-caret]');
      var panel = item.querySelector('[data-header-dropdown]');
      var link = item.querySelector('.lh-header__link');
      if (!caret || !panel) return;

      item.addEventListener('mouseenter', function () {
        if (!mqHover.matches) return;
        self._cancelClose();
        self.open(item);
        item.dataset.lhHoverOpen = '1';
      });

      item.addEventListener('mouseleave', function () {
        if (!mqHover.matches) return;
        self._scheduleClose(item);
      });

      caret.addEventListener('click', function (event) {
        event.preventDefault();
        var isOpen = item.classList.contains('is-open');
        if (isOpen && item.dataset.lhHoverOpen === '1') {
          // hover already opened it: the first click confirms and moves focus in
          delete item.dataset.lhHoverOpen;
          self.open(item, true);
          return;
        }
        if (isOpen) self.close(item);
        else self.open(item, true);
      });

      item.addEventListener('focusout', function (event) {
        var next = event.relatedTarget;
        if (next && item.contains(next)) return;
        self.close(item);
      });

      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && item.classList.contains('is-open')) {
          event.preventDefault();
          event.stopPropagation();
          self.close(item);
          caret.focus();
          return;
        }
        if (event.key === 'ArrowDown' && (event.target === caret || event.target === link)) {
          event.preventDefault();
          self.open(item, true);
          return;
        }
        if (event.key === 'ArrowUp' && event.target === caret && item.classList.contains('is-open')) {
          event.preventDefault();
          self.close(item);
        }
      });
    };

    LhHeaderNav.prototype._scheduleClose = function (item) {
      var self = this;
      this._cancelClose();
      this._closeTimer = window.setTimeout(function () { self.close(item); }, HOVER_CLOSE_DELAY);
    };

    LhHeaderNav.prototype._cancelClose = function () {
      window.clearTimeout(this._closeTimer);
      this._closeTimer = null;
    };

    LhHeaderNav.prototype.open = function (item, focusFirst) {
      if (this._openItem && this._openItem !== item) this.close(this._openItem);
      var caret = item.querySelector('[data-header-caret]');
      var panel = item.querySelector('[data-header-dropdown]');
      item.classList.add('is-open');
      if (caret) {
        caret.setAttribute('aria-expanded', 'true');
        if (caret.dataset.labelClose) caret.setAttribute('aria-label', caret.dataset.labelClose);
      }
      this._openItem = item;
      this._position(panel);
      if (focusFirst && panel) {
        var first = panel.querySelector('a, button');
        if (first) first.focus();
      }
    };

    LhHeaderNav.prototype.close = function (item) {
      if (!item) return;
      var caret = item.querySelector('[data-header-caret]');
      item.classList.remove('is-open');
      delete item.dataset.lhHoverOpen;
      if (caret) {
        caret.setAttribute('aria-expanded', 'false');
        if (caret.dataset.labelOpen) caret.setAttribute('aria-label', caret.dataset.labelOpen);
      }
      if (this._openItem === item) this._openItem = null;
    };

    LhHeaderNav.prototype.closeAll = function () {
      var self = this;
      this._items.forEach(function (item) { self.close(item); });
    };

    LhHeaderNav.prototype._position = function (panel) {
      if (!panel) return;
      panel.classList.remove('is-flipped');
      var rect = panel.getBoundingClientRect();
      var viewport = document.documentElement.clientWidth || window.innerWidth;
      if (rect.right > viewport - 8) panel.classList.add('is-flipped');
    };

    LhHeaderNav.prototype._onDocumentClick = function (event) {
      if (!this._openItem) return;
      if (this.contains(event.target)) return;
      this.close(this._openItem);
    };

    LhHeaderNav.prototype._onDocumentKeydown = function (event) {
      if (event.key === 'Escape' && this._openItem) this.close(this._openItem);
    };

    return LhHeaderNav;
  })();
  Object.setPrototypeOf(LhHeaderNav, HTMLElement);

  if (!customElements.get('lh-header-nav')) customElements.define('lh-header-nav', LhHeaderNav);

  /* ------------------------------------------------------------------------
   * Mobile drawer accordion
   * ---------------------------------------------------------------------- */
  var LhDrawerMenu = /** @class */ (function () {
    function LhDrawerMenu() {
      return Reflect.construct(HTMLElement, [], LhDrawerMenu);
    }

    LhDrawerMenu.prototype = Object.create(HTMLElement.prototype, { constructor: { value: LhDrawerMenu } });

    LhDrawerMenu.prototype.connectedCallback = function () {
      var self = this;
      this.addEventListener('click', function (event) {
        var button = event.target.closest('[data-drawer-accordion]');
        if (!button || !self.contains(button)) return;
        event.preventDefault();
        self.toggle(button);
      });
    };

    LhDrawerMenu.prototype.toggle = function (button, force) {
      var sub = document.getElementById(button.getAttribute('aria-controls'));
      if (!sub) return;
      var expanded = button.getAttribute('aria-expanded') === 'true';
      var next = typeof force === 'boolean' ? force : !expanded;
      button.setAttribute('aria-expanded', String(next));
      sub.classList.toggle('is-open', next);
      var label = next ? button.dataset.labelClose : button.dataset.labelOpen;
      if (label) button.setAttribute('aria-label', label);
    };

    return LhDrawerMenu;
  })();
  Object.setPrototypeOf(LhDrawerMenu, HTMLElement);

  if (!customElements.get('lh-drawer-menu')) customElements.define('lh-drawer-menu', LhDrawerMenu);

  /* ------------------------------------------------------------------------
   * Announcement bar rotation
   * ---------------------------------------------------------------------- */
  var LhAnnouncementBar = /** @class */ (function () {
    function LhAnnouncementBar() {
      var self = Reflect.construct(HTMLElement, [], LhAnnouncementBar);
      self._timer = null;
      self._paused = false;
      self._index = 0;
      self._onVisibility = self._onVisibility.bind(self);
      self._onBlockSelect = self._onBlockSelect.bind(self);
      self._onBlockDeselect = self._onBlockDeselect.bind(self);
      return self;
    }

    LhAnnouncementBar.prototype = Object.create(HTMLElement.prototype, { constructor: { value: LhAnnouncementBar } });

    LhAnnouncementBar.prototype.connectedCallback = function () {
      var self = this;
      this._items = toArray(this.querySelectorAll('[data-announcement-item]'));
      this._autoplay = this.getAttribute('data-autoplay') === 'true' && this._items.length > 1;
      if (!this._autoplay) return;

      if (mqReduce.matches) {
        this.classList.add('is-static');
        return;
      }

      var seconds = parseInt(this.getAttribute('data-speed'), 10);
      this._interval = Math.max(3, isNaN(seconds) ? 5 : seconds) * 1000;

      this.show(0);
      ['mouseenter', 'focusin', 'touchstart'].forEach(function (type) {
        self.addEventListener(type, function () { self.pause(); }, { passive: true });
      });
      ['mouseleave', 'focusout'].forEach(function (type) {
        self.addEventListener(type, function () { self.resume(); });
      });
      document.addEventListener('visibilitychange', this._onVisibility);
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
      this.start();
    };

    LhAnnouncementBar.prototype.disconnectedCallback = function () {
      this.stop();
      document.removeEventListener('visibilitychange', this._onVisibility);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
    };

    LhAnnouncementBar.prototype.show = function (index) {
      this._index = (index + this._items.length) % this._items.length;
      var current = this._index;
      this._items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === current);
      });
    };

    LhAnnouncementBar.prototype.next = function () {
      this.show(this._index + 1);
    };

    LhAnnouncementBar.prototype.start = function () {
      var self = this;
      this.stop();
      this._timer = window.setInterval(function () {
        if (!self._paused) self.next();
      }, this._interval);
    };

    LhAnnouncementBar.prototype.stop = function () {
      window.clearInterval(this._timer);
      this._timer = null;
    };

    LhAnnouncementBar.prototype.pause = function () { this._paused = true; };
    LhAnnouncementBar.prototype.resume = function () { if (!this._editorHold) this._paused = false; };

    LhAnnouncementBar.prototype._onVisibility = function () {
      if (document.hidden) this.stop();
      else this.start();
    };

    LhAnnouncementBar.prototype._onBlockSelect = function (event) {
      var index = this._items.indexOf(event.target);
      if (index === -1) return;
      this._editorHold = true;
      this.pause();
      this.show(index);
    };

    LhAnnouncementBar.prototype._onBlockDeselect = function (event) {
      if (this._items.indexOf(event.target) === -1) return;
      this._editorHold = false;
      this.resume();
    };

    return LhAnnouncementBar;
  })();
  Object.setPrototypeOf(LhAnnouncementBar, HTMLElement);

  if (!customElements.get('lh-announcement-bar')) customElements.define('lh-announcement-bar', LhAnnouncementBar);

  /* ------------------------------------------------------------------------
   * Drawers: aria-expanded on openers + search input focus + desktop close
   * ---------------------------------------------------------------------- */
  var observedDrawers = [];

  function setOpenerState(id, open) {
    toArray(document.querySelectorAll('[data-drawer-open="' + id + '"], [data-drawer-toggle="' + id + '"]'))
      .forEach(function (trigger) { trigger.setAttribute('aria-expanded', open ? 'true' : 'false'); });
  }

  function focusSearchInput(drawer) {
    var input = drawer.querySelector('input[type="search"]');
    if (!input) return;
    var attempt = function () {
      if (!drawer.classList.contains('is-open')) return;
      var active = document.activeElement;
      // Do not steal focus once the user started interacting inside the drawer.
      if (active && active !== document.body && drawer.contains(active) && active !== input && active.tagName !== 'BUTTON') return;
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    };
    window.setTimeout(attempt, 30);
    window.setTimeout(attempt, 360);
  }

  var raiseTimer = null;

  // The drawers are rendered inside the sticky header section wrapper
  // (z-index 40). While one is open, lift the wrapper above everything else
  // so no page element can paint over the overlay. Mirrors the CSS :has() rule.
  function updateWrapperRaise(drawer) {
    var wrap = drawer.closest('.shopify-section');
    if (!wrap) return;
    window.clearTimeout(raiseTimer);
    var anyOpen = toArray(wrap.querySelectorAll('.lh-drawer.is-open')).length > 0;
    if (anyOpen) {
      wrap.classList.add('lh-header-section--raised');
    } else {
      // keep raised during the close transition
      raiseTimer = window.setTimeout(function () {
        if (!wrap.querySelector('.lh-drawer.is-open')) wrap.classList.remove('lh-header-section--raised');
      }, 350);
    }
  }

  /* ------------------------------------------------------------------------
   * Predictive search: lazy load of assets/predictive-search.js
   * The URL sits on <lh-predictive-search data-script> (snippets/header-search.liquid);
   * loaded on first drawer open (search or menu drawer both hold a search form) or
   * as a warm-up on pointerdown/focusin over their openers. Until the file arrives
   * the form submits to /search as usual; predictive-search.js picks up text typed
   * in the meantime. No request is made when predictive search is disabled.
   * ---------------------------------------------------------------------- */
  var psRequested = false;

  function loadPredictiveSearch() {
    if (psRequested) return;
    if (window.customElements && customElements.get('lh-predictive-search')) {
      psRequested = true;
      return;
    }
    var el = document.querySelector('lh-predictive-search[data-script]:not([data-disabled])');
    var src = el && el.getAttribute('data-script');
    if (!src) return;
    psRequested = true;
    var script = document.createElement('script');
    script.src = src; // dinamički skript je async po sebi
    script.onerror = function () { psRequested = false; };
    document.head.appendChild(script);
  }

  ['pointerdown', 'focusin'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var t = event.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-drawer-open="search-drawer"], [data-drawer-open="menu-drawer"], lh-predictive-search')) {
        loadPredictiveSearch();
      }
    }, { passive: true, capture: true });
  });

  function onDrawerStateChange(drawer, open) {
    setOpenerState(drawer.id, open);
    updateWrapperRaise(drawer);
    if (open && (drawer.id === 'search-drawer' || drawer.id === 'menu-drawer')) loadPredictiveSearch();
    if (open && drawer.id === 'search-drawer') focusSearchInput(drawer);
    if (!open && drawer.id === 'menu-drawer') {
      // Reset the drawer's search state so the menu is visible next time.
      drawer.classList.remove('is-searching');
    }
  }

  function observeDrawer(drawer) {
    if (!drawer || observedDrawers.indexOf(drawer) !== -1) return;
    observedDrawers.push(drawer);
    var wasOpen = drawer.classList.contains('is-open');
    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function () {
        var isOpen = drawer.classList.contains('is-open');
        if (isOpen === wasOpen) return;
        wasOpen = isOpen;
        onDrawerStateChange(drawer, isOpen);
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    drawer.addEventListener('drawer:open', function () { onDrawerStateChange(drawer, true); });
    drawer.addEventListener('drawer:close', function () { onDrawerStateChange(drawer, false); });
  }

  function closeDrawer(id) {
    var drawer = document.getElementById(id);
    if (!drawer || !drawer.classList.contains('is-open')) return;
    if (window.Lumia && window.Lumia.drawer && typeof window.Lumia.drawer.close === 'function') {
      window.Lumia.drawer.close(id);
    }
  }

  onMedia(mqDesktop, function (event) {
    if (event.matches) {
      closeDrawer('menu-drawer');
    } else {
      toArray(document.querySelectorAll('lh-header-nav')).forEach(function (nav) {
        if (typeof nav.closeAll === 'function') nav.closeAll();
      });
    }
  });

  /* ------------------------------------------------------------------------
   * Cart button fallback (no drawer section / no global.js → cart page)
   * ---------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-header-cart]');
    if (!button) return;
    var drawerId = button.getAttribute('data-drawer-open');
    if (!drawerId) return;
    var hasDrawer = !!document.getElementById(drawerId);
    var hasApi = !!(window.Lumia && window.Lumia.drawer);
    if (hasDrawer && hasApi) return;
    var url = button.getAttribute('data-cart-url');
    if (url) {
      event.preventDefault();
      window.location.href = url;
    }
  });

  /* ------------------------------------------------------------------------
   * Init (also on theme-editor section re-render)
   * ---------------------------------------------------------------------- */
  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    toArray(scope.querySelectorAll('.lh-header')).forEach(initHeader);
    ['menu-drawer', 'search-drawer'].forEach(function (id) {
      observeDrawer(document.getElementById(id));
    });
  }

  onReady(function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
